import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminXDailyStatus,
  adminXDailySetSettings,
  adminXDailyGenerate,
  adminXDailyEdit,
  adminXDailyPost,
  adminXDailyRevise,
  adminXDailySaveTemplate,
  adminXDailyDeleteTemplate,
  adminXDailyPlaybook,
  adminXDailyToolSearch,
} from "../lib/api";

// Daily editorial X post engine. Generates a daily insight observation drawn
// from the live index. Default = manual review (edit + approve a draft); the
// "auto-post" checkbox publishes it automatically at the scheduled UTC time.
export default function DailyInsightPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [text, setText] = useState("");
  const [chatMsg, setChatMsg] = useState("");
  const [tplId, setTplId] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplLabel, setTplLabel] = useState("");
  const [tplStyle, setTplStyle] = useState("");
  const [postType, setPostType] = useState("auto");
  const [listId, setListId] = useState("");
  const [pb, setPb] = useState(null);
  const [pbBusy, setPbBusy] = useState(false);
  const [toolQ, setToolQ] = useState("");
  const [toolResults, setToolResults] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await adminXDailyStatus();
      setD(data);
      setText(data?.today?.text || "");
    } catch {
      /* surfaced elsewhere */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(load);
  }, [load]);

  const settings = d?.settings || {};
  const today = d?.today || null;
  const posted = today?.status === "posted";
  const recent = d?.recent || [];
  const templates = d?.templates || [];
  const postTypes = d?.post_types || [];
  const lists = d?.lists || [];
  const eligible = d?.eligible_tools;

  const saveSetting = async (patch) => {
    try {
      const next = await adminXDailySetSettings(patch);
      setD((s) => ({ ...s, settings: next }));
    } catch {
      toast.error("could not update setting");
    }
  };

  const generate = async () => {
    if (postType === "list" && !listId && lists.length === 0) {
      toast.error("create a curated list first (lists tab)");
      return;
    }
    setBusy("generate");
    try {
      const draft = await adminXDailyGenerate({ templateId: tplId, postType, listId });
      setD((s) => ({ ...s, today: draft }));
      setText(draft?.text || "");
      toast.success("draft generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not generate");
    } finally {
      setBusy("");
    }
  };

  const swapTools = async () => {
    setBusy("swap");
    try {
      const draft = await adminXDailyGenerate({
        templateId: today?.template_id || tplId,
        postType: today?.post_type || postType,
        listId: today?.list_id || listId,
        excludeSlugs: today?.featured_slugs || [],
      });
      setD((s) => ({ ...s, today: draft }));
      setText(draft?.text || "");
      toast.success("pulled fresh tools");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not swap tools");
    } finally {
      setBusy("");
    }
  };

  const searchTools = async (q) => {
    setToolQ(q);
    if (!q.trim()) {
      setToolResults([]);
      return;
    }
    try {
      setToolResults(await adminXDailyToolSearch(q));
    } catch {
      /* ignore */
    }
  };

  const featureTool = async (slug, title) => {
    setBusy("focus");
    setToolResults([]);
    setToolQ("");
    try {
      const draft = await adminXDailyGenerate({ templateId: today?.template_id || tplId, focusSlug: slug });
      setD((s) => ({ ...s, today: draft }));
      setText(draft?.text || "");
      toast.success(`now featuring ${title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not feature that tool");
    } finally {
      setBusy("");
    }
  };

  const loadPlaybook = async (refresh = false) => {
    setPbBusy(true);
    try {
      const data = await adminXDailyPlaybook(refresh);
      setPb(data);
      if (refresh) toast.success("metrics refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not load playbook");
    } finally {
      setPbBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy("save");
    try {
      const draft = await adminXDailyEdit(text);
      setD((s) => ({ ...s, today: draft }));
      toast.success("draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save");
    } finally {
      setBusy("");
    }
  };

  const post = async () => {
    setBusy("post");
    try {
      // Persist any unsaved textarea edits BEFORE posting, so what you see in
      // the editor is exactly what gets published.
      if (today && text.trim() && text !== today.text) {
        await adminXDailyEdit(text);
      }
      const draft = await adminXDailyPost();
      setD((s) => ({ ...s, today: draft }));
      toast.success("posted to X");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not post");
    } finally {
      setBusy("");
    }
  };

  const revise = async () => {
    const msg = chatMsg.trim();
    if (!msg) return;
    setBusy("revise");
    try {
      const draft = await adminXDailyRevise(msg);
      setD((s) => ({ ...s, today: draft }));
      setText(draft?.text || "");
      setChatMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not revise");
    } finally {
      setBusy("");
    }
  };

  const openSave = () => {
    // prefill the style from the editor's chat instructions so a proven refine
    // can be saved as a reusable preset (fact-specific bits are easily trimmed).
    const prefill = (today?.chat || [])
      .filter((m) => m.role === "user")
      .map((m) => m.content)
      .join("; ");
    setTplStyle(prefill);
    setTplLabel("");
    setSaveOpen(true);
  };

  const saveTemplate = async () => {
    setBusy("tpl-save");
    try {
      const list = await adminXDailySaveTemplate(tplLabel, tplStyle);
      setD((s) => ({ ...s, templates: list }));
      setSaveOpen(false);
      setTplLabel("");
      setTplStyle("");
      toast.success("style preset saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save preset");
    } finally {
      setBusy("");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      const list = await adminXDailyDeleteTemplate(id);
      setD((s) => ({ ...s, templates: list }));
      if (tplId === id) setTplId("");
    } catch {
      toast.error("could not delete preset");
    }
  };

  if (loading) return <div className="text-[12px] text-[#888]">loading daily engine…</div>;

  const len = text.length;

  return (
    <div data-testid="admin-x-daily" className="border-2 border-black p-3 space-y-4">
      <div className="text-[13px] font-bold">{">"} daily insight post (editorial)</div>
      <p className="text-[11px] text-[#666] m-0">
        a daily observation about the investing-tools space, drawn from your live
        index (real numbers only — no invented claims). manually approved by
        default; tick auto-post to publish automatically.
      </p>

      {/* settings row */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]">
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="x-daily-enabled">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => saveSetting({ enabled: e.target.checked })}
          />
          enable daily engine
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="x-daily-auto">
          <input
            type="checkbox"
            checked={!!settings.auto}
            disabled={!settings.enabled || !d?.configured}
            onChange={(e) => saveSetting({ auto: e.target.checked })}
          />
          auto-post (otherwise: review &amp; approve)
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="x-daily-link">
          <input
            type="checkbox"
            checked={!!settings.include_link}
            onChange={(e) => saveSetting({ include_link: e.target.checked })}
          />
          append site link
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="x-daily-link-reply">
          <input
            type="checkbox"
            checked={!!settings.link_in_reply}
            disabled={!settings.include_link}
            onChange={(e) => saveSetting({ link_in_reply: e.target.checked })}
          />
          link in first reply (max reach)
        </label>
        <label className="flex items-center gap-2">
          time (UTC):
          <input
            type="text"
            value={settings.time_utc || "14:00"}
            onChange={(e) => setD((s) => ({ ...s, settings: { ...s.settings, time_utc: e.target.value } }))}
            onBlur={(e) => saveSetting({ time_utc: e.target.value })}
            className="border border-black px-1 py-0.5 w-16 font-mono text-[12px]"
            data-testid="x-daily-time"
          />
        </label>
      </div>

      {!d?.configured && (
        <div className="text-[11px] text-[#a00]" data-testid="x-daily-not-configured">
          X API not configured — drafts still generate, but you can&apos;t post until
          OAuth 1.0a creds are set and your X balance is funded.
        </div>
      )}

      {/* style preset library */}
      <div className="border border-black p-2 space-y-2" data-testid="x-daily-styles">
        <div className="text-[11px] uppercase tracking-wide text-[#888]">
          editorial style presets (reusable post styles)
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <span>post type:</span>
          <select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            className="border border-black px-1 py-0.5 font-mono text-[12px] bg-white"
            data-testid="x-daily-type-select"
          >
            {postTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          {postType === "list" && (
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="border border-black px-1 py-0.5 font-mono text-[12px] bg-white"
              data-testid="x-daily-list-select"
            >
              <option value="">— newest saved list —</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
          )}
          {typeof eligible === "number" && (
            <span
              className={`text-[11px] ${eligible < 8 ? "text-[#a00]" : "text-[#888]"}`}
              data-testid="x-daily-eligible"
              title="tools with an X handle — the pool the daily engine rotates through"
            >
              {eligible} tool{eligible === 1 ? "" : "s"} eligible (have an X handle)
              {eligible < 8 ? " — run Admin → enrich → grab X handles to widen this" : ""}
            </span>
          )}
        </div>

        {/* Tool controls: pull different tools, or feature a specific one. */}
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <button
            className="tv-action-link border border-black px-2 py-0.5"
            onClick={swapTools}
            disabled={busy === "swap" || !today}
            title="regenerate this post type with fresh tools (excludes the ones in the current draft)"
            data-testid="x-daily-swap-btn"
          >
            {busy === "swap" ? "…" : "🔀 different tools"}
          </button>
          <div className="relative">
            <input
              type="text"
              value={toolQ}
              onChange={(e) => searchTools(e.target.value)}
              placeholder="feature a specific tool…"
              className="border border-black px-2 py-0.5 font-mono text-[12px] bg-white w-52"
              data-testid="x-daily-tool-search"
            />
            {toolResults.length > 0 && (
              <div
                className="absolute z-20 mt-0.5 w-64 max-h-56 overflow-y-auto border border-black bg-white shadow"
                data-testid="x-daily-tool-results"
              >
                {toolResults.map((t) => (
                  <button
                    key={t.slug}
                    className="block w-full text-left px-2 py-1 text-[12px] hover:bg-[#FBF8EE] border-b border-[#eee]"
                    onClick={() => featureTool(t.slug, t.title)}
                    data-testid={`x-daily-tool-opt-${t.slug}`}
                  >
                    {t.title} <span className="text-[#888]">@{t.x}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <span>generate with style:</span>
          <select
            value={tplId}
            onChange={(e) => setTplId(e.target.value)}
            className="border border-black px-1 py-0.5 font-mono text-[12px] bg-white"
            data-testid="x-daily-style-select"
          >
            <option value="">— default house style —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <button
            className="tv-action-link border border-black px-2 py-0.5"
            onClick={openSave}
            data-testid="x-daily-style-save-open"
          >
            [+ save a style]
          </button>
        </div>

        {templates.length > 0 && (
          <ul className="space-y-1 m-0 p-0 list-none" data-testid="x-daily-style-list">
            {templates.map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-[11px]">
                <span className="font-bold whitespace-nowrap">{t.label}:</span>
                <span className="text-[#666] flex-1">{t.style}</span>
                <button
                  className="tv-action-link text-[#a00] whitespace-nowrap"
                  onClick={() => deleteTemplate(t.id)}
                  data-testid={`x-daily-style-del-${t.id}`}
                >
                  [×]
                </button>
              </li>
            ))}
          </ul>
        )}

        {saveOpen && (
          <div className="border border-[#ccc] p-2 space-y-2 bg-[#FBF8EE]" data-testid="x-daily-style-form">
            <input
              type="text"
              value={tplLabel}
              onChange={(e) => setTplLabel(e.target.value)}
              placeholder="name this style (e.g. punchy spotlight)"
              className="w-full border border-black px-2 py-1 font-mono text-[12px] bg-white"
              data-testid="x-daily-style-label"
            />
            <textarea
              value={tplStyle}
              onChange={(e) => setTplStyle(e.target.value)}
              rows={2}
              placeholder="style instruction — tone/length/structure only (e.g. lead with the use case, one tool, very terse, no semicolons)"
              className="w-full border border-black px-2 py-1 font-mono text-[12px] bg-white resize-y"
              data-testid="x-daily-style-text"
            />
            <div className="flex gap-3">
              <button
                className="tv-action-link border border-black px-3 py-1"
                onClick={saveTemplate}
                disabled={busy === "tpl-save" || !tplLabel.trim() || !tplStyle.trim()}
                data-testid="x-daily-style-save-btn"
              >
                {busy === "tpl-save" ? "saving…" : "[save preset]"}
              </button>
              <button
                className="tv-action-link border border-black px-3 py-1"
                onClick={() => setSaveOpen(false)}
                data-testid="x-daily-style-cancel-btn"
              >
                [cancel]
              </button>
            </div>
          </div>
        )}
      </div>

      {/* today's draft */}
      <div className="border border-black p-2">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-[#888]">
          <span>today&apos;s draft {today?.angle ? `· ${today.angle}` : ""}</span>
          <button
            className="tv-action-link border border-black px-2"
            onClick={generate}
            disabled={busy === "generate"}
            data-testid="x-daily-generate-btn"
          >
            {busy === "generate" ? "…" : today ? "[regenerate]" : "[generate]"}
          </button>
        </div>

        {today ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="mt-2 w-full border border-black bg-[#FBF8EE] p-2 text-[12px] font-mono resize-y"
              data-testid="x-daily-text"
            />
            <div className="flex items-center justify-between text-[10px] text-[#888]">
              <span>{today.topic ? `topic: ${today.topic}` : ""}</span>
              <span className={len > 280 ? "text-[#a00]" : ""}>{len}/280</span>
            </div>
            {settings.include_link && today.link && (
              <div className="text-[10px] text-[#666] mt-1" data-testid="x-daily-link-note">
                link{" "}
                <span className="text-black">{today.link}</span>{" "}
                {settings.link_in_reply
                  ? "→ posted as the first reply (body above stays clean)"
                  : "→ appended to the post above"}
              </div>
            )}

            {posted && today.post_url && (
              <div className="text-[11px] mt-2" data-testid="x-daily-posted">
                already posted today →{" "}
                <a href={today.post_url} target="_blank" rel="noopener noreferrer" className="tv-link">
                  {today.post_url}
                </a>{" "}
                <span className="text-[#888]">(you can still edit &amp; post another, or [regenerate] a fresh one)</span>
              </div>
            )}

            <div className="mt-2 flex gap-3">
              <button
                className="tv-action-link border border-black px-3 py-1"
                onClick={saveDraft}
                disabled={busy === "save" || text === today.text}
                data-testid="x-daily-save-btn"
              >
                {busy === "save" ? "saving…" : "[save edits]"}
              </button>
              <button
                className="tv-action-link border border-black px-3 py-1"
                onClick={post}
                disabled={busy === "post" || !d?.configured}
                data-testid="x-daily-post-btn"
              >
                {busy === "post" ? "posting…" : posted ? "[post another]" : "[approve & post now]"}
              </button>
            </div>

            <div className="mt-3 border-t border-[#eee] pt-2" data-testid="x-daily-chat">
                <div className="text-[10px] uppercase tracking-wide text-[#888] mb-1">
                  chat to refine (stays bound to today&apos;s facts — no new tools invented)
                </div>
                {Array.isArray(today.chat) && today.chat.length > 0 && (
                  <div className="space-y-1 mb-2 max-h-40 overflow-y-auto" data-testid="x-daily-chat-log">
                    {today.chat.map((m, i) => (
                      <div
                        key={i}
                        className={`text-[11px] ${m.role === "user" ? "text-[#1a73e8]" : "text-black"}`}
                      >
                        <span className="text-[#888]">{m.role === "user" ? "you ›" : "ai ›"}</span>{" "}
                        {m.content}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatMsg}
                    onChange={(e) => setChatMsg(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        revise();
                      }
                    }}
                    placeholder='e.g. "make it punchier", "focus on koyfin", "tag the handles"'
                    disabled={busy === "revise"}
                    className="flex-1 border border-black px-2 py-1 font-mono text-[12px] bg-white"
                    data-testid="x-daily-chat-input"
                  />
                  <button
                    className="tv-action-link border border-black px-3 py-1 whitespace-nowrap"
                    onClick={revise}
                    disabled={busy === "revise" || !chatMsg.trim()}
                    data-testid="x-daily-chat-send-btn"
                  >
                    {busy === "revise" ? "…" : "[refine]"}
                  </button>
                </div>
              </div>
          </>
        ) : (
          <div className="mt-2 text-[12px] text-[#888]" data-testid="x-daily-no-draft">
            no draft yet — hit [generate] to preview today&apos;s insight.
          </div>
        )}
      </div>

      {/* history */}
      {recent.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[#888]">recent daily posts</div>
          <table className="mt-1 w-full text-[12px] border-collapse" data-testid="x-daily-recent">
            <tbody>
              {recent.map((p) => (
                <tr key={p.date} className="border-b border-[#eee] align-top">
                  <td className="py-1 pr-2 text-[#666] whitespace-nowrap">{p.date}</td>
                  <td className="py-1 pr-2 text-[#888] whitespace-nowrap">{p.angle}</td>
                  <td className="py-1 pr-2">{p.text}</td>
                  <td className="py-1 whitespace-nowrap">
                    {p.status === "posted" ? (
                      <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="tv-link">view</a>
                    ) : (
                      <span className="text-[#a60]">draft</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* engagement playbook */}
      <div className="border border-black p-2 space-y-2" data-testid="x-daily-playbook">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-[#888]">
            engagement playbook — which post types &amp; styles perform
          </span>
          <div className="flex gap-2">
            <button
              className="tv-action-link border border-black px-2 py-0.5 text-[11px]"
              onClick={() => loadPlaybook(false)}
              disabled={pbBusy}
              data-testid="x-daily-playbook-load"
            >
              {pbBusy ? "…" : pb ? "[reload cached]" : "[load]"}
            </button>
            <button
              className="tv-action-link border border-black px-2 py-0.5 text-[11px]"
              onClick={() => loadPlaybook(true)}
              disabled={pbBusy || !d?.configured}
              title="pulls fresh numbers from X (small read cost on pay-per-use)"
              data-testid="x-daily-playbook-refresh"
            >
              {pbBusy ? "…" : "[refresh metrics ↻]"}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-[#888] m-0">
          metrics are pulled from X only when you hit refresh (a small read cost),
          then cached. numbers appear once you&apos;ve posted tweets.
        </p>

        {pb && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#888] mb-1">by post type</div>
              {pb.by_type?.length ? (
                <table className="w-full text-[11px] border-collapse" data-testid="x-daily-playbook-bytype">
                  <thead>
                    <tr className="text-[#888] text-left">
                      <th className="font-normal py-0.5 pr-2">type</th>
                      <th className="font-normal py-0.5 pr-2">posts</th>
                      <th className="font-normal py-0.5 pr-2">avg eng.</th>
                      <th className="font-normal py-0.5">avg impr.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pb.by_type.map((r) => (
                      <tr key={r.key} className="border-b border-[#eee]">
                        <td className="py-0.5 pr-2">{r.key}</td>
                        <td className="py-0.5 pr-2">{r.posts}</td>
                        <td className="py-0.5 pr-2">{r.avg_engagement ?? "—"}</td>
                        <td className="py-0.5">{r.avg_impressions ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-[11px] text-[#888]">no posted tweets yet</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#888] mb-1">by style preset</div>
              {pb.by_style?.length ? (
                <table className="w-full text-[11px] border-collapse" data-testid="x-daily-playbook-bystyle">
                  <thead>
                    <tr className="text-[#888] text-left">
                      <th className="font-normal py-0.5 pr-2">style</th>
                      <th className="font-normal py-0.5 pr-2">posts</th>
                      <th className="font-normal py-0.5 pr-2">avg eng.</th>
                      <th className="font-normal py-0.5">avg impr.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pb.by_style.map((r) => (
                      <tr key={r.key} className="border-b border-[#eee]">
                        <td className="py-0.5 pr-2">{r.key}</td>
                        <td className="py-0.5 pr-2">{r.posts}</td>
                        <td className="py-0.5 pr-2">{r.avg_engagement ?? "—"}</td>
                        <td className="py-0.5">{r.avg_impressions ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-[11px] text-[#888]">no posted tweets yet</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
